/**
 * Service for fetching Performance Agent insights from the backend
 */

import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:7000';

// Hardcoded Job Config IDs - exported for reuse across components
export const MMM_JOB_CONFIG_ID = 'c14a6f9f-a8e3-4691-8b93-cc25c0d09d43';
export const GEOLIFT_JOB_CONFIG_IDS = [
  '60506bea-2be1-4f42-b23d-341c4a655e2e',
  '21fdd893-0467-48b7-863a-36b9266dd1f9'
];
export const BID_JOB_CONFIG_ID = '1f829a59-f7b6-4872-bc4a-160e1113d27d';

export interface InsightData {
  channel: string;
  spend: number;
  effect_pct: number;
  efficiency: number;
}

export interface MMMInsightResponse {
  analysis: string;
  metadata: {
    channels: InsightData[];
    total_spend: number;
    total_effect: number;
  };
  data: InsightData[];
}

export interface Insight {
  id: string;
  category: 'channel' | 'campaign' | 'geo' | 'segment';
  type: 'issue' | 'opportunity';
  title: string;
  metric: string;
  value: string;
  change?: number; 
  suggestion: string;
  priority: 'high' | 'medium' | 'low';
}

/**
 * Fetch Performance Agent Insights from backend
 * Tenant ID is handled by backend middleware
 * Job Config ID is hardcoded
 */
export const fetchPerformanceAgentInsights = async (): Promise<Insight[]> => {
  try {
    const response = await axios.post(
      `/api/performanceAgentInsight`,
      {
        jobConfigIdMMM: MMM_JOB_CONFIG_ID,
        jobConfigIdGeoLift: GEOLIFT_JOB_CONFIG_IDS,
        jobConfigIdBid: BID_JOB_CONFIG_ID, // Include bid job config ID
      },
      {
        headers: {
          'Content-Type': 'application/json',
        },
      }
    );

    // Backend returns { status: "success", data: { mmm_insight: { analysis: [...] }, campaign_level_insights: {...}, geolift_insight: { deterministic: [...] } } }
    const responseData = response.data?.data;

    if (responseData) {
      const insights: Insight[] = [];

      // Parse MMM insights - analysis is now a JSON array, not a string
      if (responseData.mmm_insight?.analysis) {
        const mmmAnalysis = responseData.mmm_insight.analysis;

        // Check if analysis is already an array (new JSON format)
        if (Array.isArray(mmmAnalysis)) {
          mmmAnalysis.forEach((item: any) => {
            insights.push({
              id: `mmm-${item.id}`,
              category: 'channel',
              type: item.type || 'opportunity',
              title: item.title || '',
              metric: item.metric || 'ROI',
              value: typeof item.value === 'number' ? `${item.value.toFixed(2)}x` : String(item.value),
              suggestion: item.suggestion || item.detailInsight || '',
              priority: item.priority || 'medium',
            });
          });
        }
      }

      // Parse GeoLift insights (now contains campaign-level analysis)
      if (responseData.geolift_insight?.analysis) {
        const geoInsights = responseData.geolift_insight.analysis;

        if (Array.isArray(geoInsights)) {
          geoInsights.forEach((item: any) => {
            insights.push({
              id: `campaign-${item.id}`,
              category: 'campaign',
              type: item.type || 'opportunity',
              title: item.title || '',
              metric: item.metric || 'iROAS',
              value: typeof item.value === 'number' ? `${item.value.toFixed(2)}` : String(item.value),
              suggestion: item.suggestion || item.detailInsight || '',
              priority: item.priority || 'medium',
            });
          });
        }
      }

      // Parse Bid insights
      if (responseData.bid_insight?.summary) {
        const bidInsights = responseData.bid_insight.summary;

        if (Array.isArray(bidInsights)) {
          bidInsights.forEach((item: any, index: number) => {
            // Determine the display value based on metric type
            let displayValue = '';
            if (item.metric === 'skew' && typeof item.value === 'number') {
              // For skew metric, show the skew value without 'x' suffix
              displayValue = `${item.value.toFixed(2)}`;
            } else if (item.metric === 'numSegments' && typeof item.value === 'number') {
              // For number of segments, show just the count (label already says "Segment Count")
              displayValue = `${item.value.toLocaleString()}`;
            } else {
              // Fallback to string value
              displayValue = String(item.value || 'N/A');
            }

            insights.push({
              id: `bid-${item.id || index}`,
              category: item.category || 'segment',
              type: item.type || 'opportunity',
              title: item.title || 'Bid Analysis',
              metric: item.metric || 'Bid Adjustment',
              value: displayValue,
              suggestion: item.suggestion || '',
              priority: item.priority || 'medium',
            });
          });
        }
      }

      return insights;
    }

    return [];
  } catch (error: any) {
    console.error('Error fetching Performance Agent insights:', error);
    throw new Error(error.response?.data?.error || 'Failed to fetch Performance Agent insights');
  }
};

/**
 * Fetch MMM Results (Spend & Effect) data from backend
 */
export const fetchMMMInsights = async (
  tenantId: string,
  jobConfigId: string,
  mediaVariables: string[],
  impressionVariables: string[],
  targetVariables: string[],
  dateVariables: string[]
): Promise<MMMInsightResponse> => {
  try {
    const response = await axios.post<{ data: MMMInsightResponse }>(
      `${API_BASE_URL}/performanceAgent/mmmResultsSpendEffect`,
      {
        message: 'Analyze spend vs effect for all channels',
        tenantId,
        jobConfigId,
        media_variables: mediaVariables,
        impression_variables: impressionVariables,
        target_variables: targetVariables,
        date_variables: dateVariables,
      },
      {
        headers: {
          'Content-Type': 'application/json',
        },
      }
    );

    return response.data.data;
  } catch (error: any) {
    console.error('Error fetching MMM insights:', error);
    throw new Error(error.response?.data?.error || 'Failed to fetch insights');
  }
};

/**
 * Transform backend data into Insight cards
 */
export const transformToInsights = (data: InsightData[]): Insight[] => {
  if (!data || data.length === 0) return [];

  const insights: Insight[] = [];

  // Calculate average efficiency for comparison
  const avgEfficiency = data.reduce((sum, d) => sum + d.efficiency, 0) / data.length;
  const avgSpend = data.reduce((sum, d) => sum + d.spend, 0) / data.length;

  data.forEach((channel, index) => {
    const efficiencyDiff = ((channel.efficiency - avgEfficiency) / avgEfficiency) * 100;
    const spendDiff = ((channel.spend - avgSpend) / avgSpend) * 100;

    // Determine if it's an issue or opportunity
    const isOpportunity = channel.efficiency > avgEfficiency * 1.2; // 20% above average
    const isIssue = channel.efficiency < avgEfficiency * 0.8; // 20% below average

    if (isOpportunity) {
      insights.push({
        id: `opportunity-${index}`,
        category: 'channel',
        type: 'opportunity',
        title: `${channel.channel} Showing Strong Efficiency`,
        metric: 'Efficiency',
        value: `${channel.efficiency.toFixed(2)}x`,
        change: Math.round(efficiencyDiff),
        suggestion: `Scale ${channel.channel} budget by 20-30% - it's delivering ${channel.effect_pct.toFixed(1)}% effect with only ${channel.spend.toFixed(1)}% spend`,
        priority: channel.efficiency > avgEfficiency * 1.5 ? 'high' : 'medium',
      });
    } else if (isIssue) {
      insights.push({
        id: `issue-${index}`,
        category: 'channel',
        type: 'issue',
        title: `${channel.channel} is Underperforming`,
        metric: 'Efficiency',
        value: `${channel.efficiency.toFixed(2)}x`,
        change: Math.round(efficiencyDiff),
        suggestion: `Consider reallocating budget from ${channel.channel} - it's using ${channel.spend.toFixed(1)}% spend for only ${channel.effect_pct.toFixed(1)}% effect`,
        priority: channel.efficiency < avgEfficiency * 0.5 ? 'high' : 'medium',
      });
    }
  });

  return insights;
};

export const insightsService = {
  fetchPerformanceAgentInsights,
  fetchMMMInsights,
  transformToInsights,
};

export default insightsService;
